import React, { useMemo, useCallback } from "react";
import { RadioGroup, Radio, FormControlLabel, Checkbox } from "@mui/material";
import { Subtask, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import { ContentStatus } from "./SubtaskComponent";
import ContentStatusIndicator from "./ContentStatusIndicator";

interface CommonContentListProps {
  subtask: Subtask;
  filteredContent: Array<{
    content_id: string;
    text: string;
    time: string;
    level: string;
    type: typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE];
    playback_info?: string;
    otp?: string;
    content?: string;
  }>;
  contentStatus?: ContentStatus | null;
  getSelectedIds: () => string[];
  onContentSelected: (contentId: string, level: string, time: string) => void;
  onVideoPreview?: (videoId: string) => void;
  onNotesPreview?: (notesId: string) => void;
  availableContent?: any;
}

/**
 * CommonContentList Component
 * Displays content lists for MCQ, Coding, Video, and Notes types
 * Handles both select mode (checkboxes for MCQ/Coding) and radio selection (for Video/Notes)
 */
const CommonContentList: React.FC<CommonContentListProps> = ({
  subtask,
  filteredContent,
  contentStatus,
  getSelectedIds,
  onContentSelected,
  onVideoPreview,
  onNotesPreview,
  availableContent
}) => {
  // All hooks must be called at the top level, before any conditional returns
  const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING;
  
  // Memoized values for MCQ/Coding
  const selectedIds = getSelectedIds();
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds.join(',')]);
  const safeSelectedLevelMcq = subtask.selectedContentLevel || "";
  
  // Memoized values for Video/Notes
  const currentId = useMemo(() => 
    (subtask.id && String(subtask.id).trim()) ? String(subtask.id).trim() : "", 
    [subtask.id]
  );
  const safeSelectedLevel = subtask.selectedContentLevel || "";
  const subtaskTypeUpper = useMemo(() => subtask.subtaskType.toUpperCase(), [subtask.subtaskType]);
  
  // Create a map for O(1) lookup instead of O(n) find
  const contentMap = useMemo(() => {
    const map = new Map<string, typeof filteredContent[0]>();
    filteredContent.forEach(item => {
      if (item?.content_id) {
        map.set(String(item.content_id).trim(), item);
      }
    });
    return map;
  }, [filteredContent]);

  const handleRadioChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = e.target.value;
    if (currentId === selectedId) {
      onContentSelected("", safeSelectedLevel, "");
    } else {
      const selectedItem = contentMap.get(selectedId);
      onContentSelected(
        selectedId,
        safeSelectedLevel,
        selectedItem?.time || ""
      );
    }
  }, [currentId, safeSelectedLevel, contentMap, onContentSelected]);

  const handlePreviewClick = useCallback((item: typeof filteredContent[0]) => {
    if (!item?.content_id) return;
    
    if (item.type === SUBTASK_TYPE.VIDEO && onVideoPreview) {
      onVideoPreview(String(item.content_id));
    } else if (item.type === SUBTASK_TYPE.NOTES && onNotesPreview) {
      onNotesPreview(String(item.content_id));
    }
  }, [onVideoPreview, onNotesPreview]);

  // Early return after all hooks
  if (filteredContent.length === 0) {
    return <div className="text-muted">No content available for this level</div>;
  }

  // MCQ or Coding: Use checkboxes for multiple selection
  if (isMcqCoding) {
    return (
      <div>
        {filteredContent.map((item) => {
          if (!item?.content_id) return null; // Skip invalid items
          
          const itemId = String(item.content_id).trim();
          const isChecked = selectedIdsSet.has(itemId);
          
          return (
            <div key={item.content_id} className="d-flex mb-2 align-items-start">
              <div className="d-flex align-items-start p-2 border rounded flex-grow-1">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => {
                        const newIds = e.target.checked
                          ? [...selectedIds.filter(id => id !== itemId), itemId]
                          : selectedIds.filter(id => id !== itemId);
                        onContentSelected(newIds.join(","), safeSelectedLevelMcq, "");
                      }}
                    />
                  }
                  label={
                    <div style={{ marginLeft: "8px", fontSize: "12px", marginTop: "4px" }}>
                      {item.text || item.content_id}
                    </div>
                  }
                />
              </div>
              <ContentStatusIndicator
                contentId={itemId}
                contentType={subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]}
                contentStatus={contentStatus}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <RadioGroup value={currentId} onChange={handleRadioChange}>
      {filteredContent.map((item) => {
        if (!item?.content_id) return null; // Skip invalid items
        
        const itemId = String(item.content_id).trim();
        const isVideoOrNotes = subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES;
        
        return (
          <div key={item.content_id} className="d-flex justify-content-between mb-2 align-items-center">
            <div className="d-flex align-items-center p-2 border rounded flex-grow-1">
              <FormControlLabel
                value={itemId}
                control={<Radio />}
                label={
                  <div>
                    <strong className="ms-2">
                      {subtaskTypeUpper}
                    </strong>
                    {" - "}
                    {item.text}
                  </div>
                }                                
              />
            </div>
            <span className="d-flex align-items-center gap-2">
              <strong>{item.time || "0"}m</strong>
              {isVideoOrNotes && (
                <button
                  className="border border-none rounded-2"
                  onClick={() => handlePreviewClick(item)}
                  style={{ backgroundColor: "#F1F2FF", padding: "4px 12px" }}
                >
                  Preview
                </button>
              )}
              <ContentStatusIndicator
                contentId={itemId}
                contentType={subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]}
                contentStatus={contentStatus}
              />
            </span>
          </div>
        );
      })}
    </RadioGroup>
  );
};

export default CommonContentList;
